import { useState, useEffect, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { SidebarMenu } from "@/components/bss/SidebarMenu";
import { DashboardContent } from "@/components/bss/DashboardContent";
import { UploadPdfDialog } from "@/components/bss/UploadPdfDialog";
import { SearchDialog } from "@/components/bss/SearchDialog";
import { LoginPage } from "@/components/bss/LoginPage";
import { OnboardingModal } from "@/components/bss/OnboardingModal";
import { NotificationBell } from "@/components/bss/NotificationBell";
import { ErrorBoundary } from "@/components/bss/ErrorBoundary";
import { StreakBadge } from "@/components/bss/StreakBadge";
import { Search, Menu, User, LogOut, Download, X } from "lucide-react";
import { t } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global axios interceptor: handle expired/invalid token gracefully.
// When backend returns 401 on a request that carried an Authorization header,
// we clear the session and force re-login. Login attempts (no Authorization
// header) are excluded so wrong-password errors bubble up normally.
let _sessionExpiredHandled = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const hadAuth = !!(error?.config?.headers?.Authorization || error?.config?.headers?.common?.Authorization);
      if (status === 401 && hadAuth && !_sessionExpiredHandled) {
        const detail = error?.response?.data?.detail || '';
        // Only act on token-related 401, not other auth-required failures
        if (typeof detail === 'string' && (detail.toLowerCase().includes('token') || detail.toLowerCase().includes('not authenticated') || detail.toLowerCase().includes('invalid'))) {
          _sessionExpiredHandled = true;
          try {
            localStorage.removeItem('bss.token');
            localStorage.removeItem('bss.authUser');
            delete axios.defaults.headers.common['Authorization'];
          } catch { /* noop */ }
          toast.error('Sesi anda telah tamat. Sila log masuk semula.', { duration: 4000 });
          // Give the toast a moment to render before reload
          setTimeout(() => { window.location.href = '/'; }, 1200);
        }
      }
    } catch { /* noop */ }
    return Promise.reject(error);
  }
);

// localStorage helpers
const loadJSON = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) || fallback; }
  catch { return fallback; }
};
const loadString = (key, fallback) => {
  try { return localStorage.getItem(key) || fallback; }
  catch { return fallback; }
};
const saveJSON = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
};
const saveString = (key, value) => {
  try { localStorage.setItem(key, value); } catch {}
};

// Update streak on load
const updateStreak = () => {
  try {
    const streak = loadJSON('bss.streak', { count: 0, lastDate: '' });
    const today = new Date().toISOString().split('T')[0];
    if (streak.lastDate === today) return;
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    if (streak.lastDate === yesterday) {
      streak.count += 1;
    } else {
      streak.count = 1;
    }
    streak.lastDate = today;
    saveJSON('bss.streak', streak);
  } catch {}
};

const SyllabusPage = () => {
  // Auth state
  const [authToken, setAuthToken] = useState(() => loadString('bss.token', ''));
  const [authUser, setAuthUser] = useState(() => loadJSON('bss.authUser', null));
  const [organAccess, setOrganAccess] = useState({ unlocked: [], all_unlocked: false });

  // PWA Install Prompt
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (deferredPrompt && !loadJSON('bss.installDismissed', false)) {
      const showTimer = setTimeout(() => {
        setShowInstallBanner(true);
        // Auto-dismiss after 8s if user doesn't interact
        const dismissTimer = setTimeout(() => setShowInstallBanner(false), 8000);
        return () => clearTimeout(dismissTimer);
      }, 5000);
      return () => clearTimeout(showTimer);
    }
  }, [deferredPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') setShowInstallBanner(false);
    setDeferredPrompt(null);
  };

  const dismissInstall = () => {
    setShowInstallBanner(false);
    saveJSON('bss.installDismissed', true);
  };

  // Set axios default auth header whenever token changes
  useEffect(() => {
    if (authToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [authToken]);

  // Fetch user's organ access whenever auth changes
  useEffect(() => {
    if (!authToken) { setOrganAccess({ unlocked: [], all_unlocked: false }); return; }
    axios.get(`${API}/me/organ-access`)
      .then(res => setOrganAccess(res.data || { unlocked: [], all_unlocked: false }))
      .catch(() => {});
  }, [authToken]);

  // Handle Google OAuth callback token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      // Save token and fetch user
      saveString('bss.token', urlToken);
      setAuthToken(urlToken);
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${urlToken}` } })
        .then(res => {
          setAuthUser(res.data);
          saveJSON('bss.authUser', res.data);
          saveString('bss.studentName', res.data.name || '');
          saveString('bss.studentNiche', res.data.niche || '');
          saveString('bss.studentBatch', res.data.batch || '');
        })
        .catch(() => {
          saveString('bss.token', '');
          setAuthToken('');
        });
      // Clean URL
      window.history.replaceState({}, '', '/');
    }
  }, []);

  const [syllabus, setSyllabus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("dashboard");

  // Language
  const [lang, setLang] = useState(() => loadString('bss.lang', 'ms'));
  const toggleLang = () => {
    const next = lang === 'ms' ? 'en' : 'ms';
    setLang(next);
    saveString('bss.lang', next);
  };

  // Student info derived from auth user
  const [studentName, setStudentName] = useState(() => {
    const user = loadJSON('bss.authUser', null);
    return user?.name || loadString('bss.studentName', '');
  });
  const [studentNiche, setStudentNiche] = useState(() => {
    const user = loadJSON('bss.authUser', null);
    return user?.niche || loadString('bss.studentNiche', '');
  });
  const [studentBatch, setStudentBatch] = useState(() => {
    const user = loadJSON('bss.authUser', null);
    return user?.batch || loadString('bss.studentBatch', '');
  });
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [nicheInput, setNicheInput] = useState('');
  const [batchInput, setBatchInput] = useState('');
  const [nicheList, setNicheList] = useState([]);

  // Read progress (localStorage)
  const [readPoints, setReadPoints] = useState(() => loadJSON('bss.readPoints', {}));
  // Personal notes (localStorage)
  const [personalNotes, setPersonalNotes] = useState(() => loadJSON('bss.personalNotes', {}));
  // Bookmarks
  const [bookmarkedIds, setBookmarkedIds] = useState(() => loadJSON('bss.bookmarkedIds', {}));
  // Onboarding
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Force light mode - ensure no leftover dark class from prior sessions
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    try { localStorage.removeItem('bss.darkMode'); } catch (_e) { /* noop */ }
  }, []);

  // Update streak
  useEffect(() => { updateStreak(); }, []);

  const fetchSyllabus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/syllabus`);
      setSyllabus(response.data.syllabus);
    } catch (e) {
      console.error("Failed to fetch syllabus", e);
      toast.error("Gagal memuat syllabus");
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch syllabus whenever auth token changes (post-login, post-logout, post-OAuth callback)
  useEffect(() => {
    if (!authToken) {
      setSyllabus([]);
      setLoading(false);
      return;
    }
    fetchSyllabus();
    // Fetch niche list
    axios.get(`${API}/students/niches`).then(res => setNicheList(res.data)).catch(() => {});
  }, [authToken, fetchSyllabus]);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Toggle read point
  const handleToggleRead = useCallback((pointId) => {
    let wasAlreadyRead = false;
    setReadPoints(prev => {
      const next = { ...prev };
      if (next[pointId]) {
        wasAlreadyRead = true;
        delete next[pointId];
      } else {
        next[pointId] = true;
      }
      saveJSON('bss.readPoints', next);
      return next;
    });
    // Award XP only when newly marked as read (not when un-marking)
    if (!wasAlreadyRead) {
      axios.post(`${API}/me/award-xp?action=point_read`).then(res => {
        const newAchievements = res.data?.new_achievements || [];
        newAchievements.forEach(a => {
          toast.success(`${a.icon} ${a.title} - +${a.xp} XP`, { duration: 4500 });
        });
      }).catch(() => { /* silent */ });
    }
  }, []);

  // Sync reading progress to backend (debounced)
  useEffect(() => {
    if (!studentName || !syllabus || syllabus.length === 0) return;
    const orgSections = syllabus.filter(n => n.title !== 'SLIDE');
    const timer = setTimeout(() => {
      const ids = Object.keys(readPoints).filter(k => readPoints[k]);
      const totalPts = orgSections.reduce((acc, org) => {
        return acc + (org.children || []).reduce((a, t) => a + (t.children || []).length, 0);
      }, 0);
      axios.post(`${API}/students/sync-progress`, {
        student_name: studentName,
        read_point_ids: ids,
        total_points: totalPts,
      }).catch(() => {});
    }, 2000);
    return () => clearTimeout(timer);
  }, [readPoints, studentName, syllabus]);

  // Save personal note
  // Update profile callback
  const handleUpdateProfile = useCallback((name, niche, batch) => {
    setStudentName(name);
    setStudentNiche(niche);
    setStudentBatch(batch);
    saveString('bss.studentName', name);
    saveString('bss.studentNiche', niche);
    saveString('bss.studentBatch', batch);
  }, []);


  const handleSaveNote = useCallback((pointId, text) => {
    setPersonalNotes(prev => {
      const next = { ...prev };
      if (text.trim()) next[pointId] = text.trim();
      else delete next[pointId];
      saveJSON('bss.personalNotes', next);
      return next;
    });
  }, []);

  // Toggle bookmark
  const handleToggleBookmark = useCallback((pointId, pointTitle, pointNotes, topicTitle, organTitle) => {
    setBookmarkedIds(prev => {
      const next = { ...prev };
      if (next[pointId]) {
        delete next[pointId];
        // Remove from backend
        if (studentName) {
          axios.post(`${API}/bookmarks/toggle`, { student_name: studentName, point_id: pointId }).catch(() => {});
        }
      } else {
        next[pointId] = true;
        // Save to backend
        if (studentName) {
          axios.post(`${API}/bookmarks/toggle`, {
            student_name: studentName,
            point_id: pointId,
            point_title: pointTitle || '',
            point_notes: pointNotes || '',
            topic_title: topicTitle || '',
            organ_title: organTitle || '',
          }).catch(() => {});
        }
      }
      saveJSON('bss.bookmarkedIds', next);
      return next;
    });
  }, [studentName]);

  // Calculate read progress per organ
  const organSections = useMemo(() => syllabus.filter(n => n.title !== 'SLIDE'), [syllabus]);

  const readProgress = useMemo(() => {
    const progress = {};
    organSections.forEach(organ => {
      let total = 0;
      let read = 0;
      (organ.children || []).forEach(topic => {
        (topic.children || []).forEach(point => {
          total++;
          if (readPoints[point.id]) read++;
        });
      });
      progress[organ.id] = {
        total,
        read,
        percent: total > 0 ? Math.round((read / total) * 100) : 0,
      };
    });
    return progress;
  }, [organSections, readPoints]);

  // Save last location when navigating to an organ
  const handleSectionChange = useCallback((section) => {
    setActiveSection(section);
    setSidebarOpen(false);
    // Track last location
    const organ = organSections.find(o => o.id === section);
    if (organ) {
      const idx = organSections.indexOf(organ);
      saveJSON('bss.lastLocation', {
        organId: organ.id,
        organIdx: idx,
        topicTitle: organ.title.replace(/ORGAN \d+\s*–\s*/, ''),
        ts: Date.now(),
      });
    }
  }, [organSections]);

  // Search navigate
  const handleSearchNavigate = useCallback((organId, topicIdx, pointId) => {
    setActiveSection(organId);
    // Save last location
    const organ = organSections.find(o => o.id === organId);
    if (organ) {
      saveJSON('bss.lastLocation', {
        organId: organ.id,
        organIdx: organSections.indexOf(organ),
        topicTitle: organ.title.replace(/ORGAN \d+\s*–\s*/, ''),
        ts: Date.now(),
      });
    }
  }, [organSections]);

  const handleUploadSuccess = () => {
    fetchSyllabus();
    toast.success("PDF berjaya dimuat naik!");
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/files/${fileId}`);
      toast.success("Fail berjaya dipadam");
      fetchSyllabus();
    } catch (e) {
      toast.error("Gagal memadamkan fail");
    }
  };

  const handleAddNode = async (parentId, title, type) => {
    try {
      await axios.post(`${API}/nodes`, { title, type, parent_id: parentId, order: 0 });
      toast.success("Nod berjaya ditambah");
      fetchSyllabus();
    } catch (e) {
      toast.error("Gagal menambah nod");
    }
  };

  const handleDeleteNode = async (nodeId) => {
    try {
      await axios.delete(`${API}/nodes/${nodeId}`);
      toast.success("Nod berjaya dipadam");
      fetchSyllabus();
    } catch (e) {
      toast.error("Gagal memadamkan nod");
    }
  };

  // Flatten nodes for upload selector
  const flattenNodes = (nodes, depth = 0) => {
    let result = [];
    for (const node of nodes) {
      result.push({ ...node, depth });
      if (node.children && node.children.length > 0) {
        result = result.concat(flattenNodes(node.children, depth + 1));
      }
    }
    return result;
  };
  const allNodes = flattenNodes(syllabus);

  // Count stats
  const totalOrgans = organSections.length;
  const totalFiles = allNodes.reduce((acc, n) => acc + (n.files ? n.files.length : 0), 0);
  const totalTopics = allNodes.filter(n => n.type === 'topic').length;

  // Get active section title for topbar
  const getPageTitle = () => {
    if (activeSection === 'dashboard') return t('dashboard', lang);
    if (activeSection === 'profile') return lang === 'en' ? 'My Profile' : 'Profil Saya';
    if (activeSection === 'bookmarks') return lang === 'en' ? 'My Bookmarks' : 'Simpanan Saya';
    if (activeSection === 'quiz') return t('quiz', lang);
    if (activeSection === 'leaderboard') return t('leaderboard', lang);
    if (activeSection === 'library') return 'BSS Library';
    if (activeSection === 'faq') return 'FAQ';
    if (activeSection === 'community') return lang === 'en' ? 'Community' : 'Komuniti';
    if (activeSection === 'admin') return lang === 'en' ? 'Admin Panel' : 'Panel Admin';
    if (activeSection === 'progress') return t('learningProgress', lang);
    if (activeSection === 'test') return 'TEST';
    const organ = organSections.find(o => o.id === activeSection);
    if (organ) {
      const idx = organSections.indexOf(organ) + 1;
      return `Organ ${idx}`;
    }
    return 'BSS Portal';
  };

  // Auth handlers
  const handleLogin = useCallback((token, user) => {
    setAuthToken(token);
    setAuthUser(user);
    saveString('bss.token', token);
    saveJSON('bss.authUser', user);
    setStudentName(user.name || '');
    setStudentNiche(user.niche || '');
    setStudentBatch(user.batch || '');
    saveString('bss.studentName', user.name || '');
    saveString('bss.studentNiche', user.niche || '');
    saveString('bss.studentBatch', user.batch || '');
    // If user came from a QR attendance link, redirect back
    const redirect = localStorage.getItem('bss.post_login_redirect');
    if (redirect) {
      localStorage.removeItem('bss.post_login_redirect');
      window.location.href = redirect;
      return;
    }
    // Auto-navigate founders to their dashboard
    if (user.role === 'founder') {
      setActiveSection('founder');
    }
    // Show onboarding for first time - check backend flag too
    const onboarded = loadJSON('bss.onboarded', false);
    const serverOnboarded = user.onboarded || false;
    if (!onboarded && !serverOnboarded) {
      setTimeout(() => setShowOnboarding(true), 500);
    } else if (serverOnboarded && !onboarded) {
      saveJSON('bss.onboarded', true);
    }
  }, []);

  const handleLogout = useCallback(() => {
    setAuthToken('');
    setAuthUser(null);
    saveString('bss.token', '');
    saveJSON('bss.authUser', null);
  }, []);

  // If not logged in, show login page
  if (!authToken || !authUser) {
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <div className="fixed bottom-4 left-0 right-0 text-center z-10">
          <p className="text-[10px] font-medium tracking-[0.18em] uppercase text-[#94A3B8]">© 2026 BSS Portal</p>
        </div>
        <Toaster position="bottom-right" />
      </>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 text-sm">{t('loadingSyllabus', lang)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Auditor mode banner (visible only for role=auditor) */}
      {authUser?.role === 'auditor' && (
        <div
          className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-[#0A0A0A] py-1.5 px-4 text-center text-[11px] sm:text-xs font-bold tracking-wide flex items-center justify-center gap-2 shadow-sm"
          data-testid="auditor-mode-banner"
        >
          <span>🛡️ AUDITOR MODE - Read-Only Access</span>
          {authUser?.expires_at && (
            <span className="opacity-75 hidden sm:inline">
              · Expires {new Date(authUser.expires_at).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>
      )}
      <div className={`min-h-screen bg-gray-50 transition-colors duration-300 ${authUser?.role === 'auditor' ? 'pt-7 sm:pt-8' : ''}`}>
        {/* Sidebar */}
        <SidebarMenu
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          organSections={organSections}
          totalFiles={totalFiles}
          readProgress={readProgress}
          lang={lang}
          userRole={authUser?.role || 'student'}
          organAccess={organAccess}
          onToggleLang={toggleLang}
        />

        {/* Main area */}
        <div className="main-content-wrapper">
          {/* Top bar */}
          <header className="topbar">
            <div className="topbar-left">
              <button
                className="hamburger-btn"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-toggle"
              >
                <Menu size={22} />
              </button>
              {/* Mobile-only title (desktop shows title in content hero) */}
              <h2 className="sm:hidden text-base font-semibold text-[#0A0A0A] dark:text-gray-200" style={{ fontFamily: 'Inter, sans-serif' }}>
                {getPageTitle()}
              </h2>
            </div>
            <div className="flex items-center gap-1.5">
              {/* Search button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 px-3.5 py-2 rounded-full text-sm text-[rgba(0,0,0,0.45)] bg-[#F7F7F7] hover:bg-[#EFEFEF] dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors duration-100 min-w-[180px] sm:min-w-[240px]"
                data-testid="search-trigger"
              >
                <Search size={15} />
                <span className="text-xs flex-1 text-left">{lang === 'en' ? 'Search content, members, quiz...' : 'Cari kandungan, pelajar, quiz...'}</span>
                <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-white rounded border border-[rgba(0,0,0,0.06)] font-mono text-[rgba(0,0,0,0.4)]">
                  {/* OS-aware shortcut hint */}
                  {(typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent)) ? '⌘K' : 'Ctrl K'}
                </kbd>
              </button>

              {/* Divider - desktop only */}
              <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-0.5 hidden sm:block" />

              {/* Language toggle - desktop only */}
              <button
                onClick={toggleLang}
                className="hidden sm:block px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-100"
                data-testid="lang-toggle"
                title={lang === 'ms' ? 'Switch to English' : 'Tukar ke Bahasa Melayu'}
              >
                {lang === 'ms' ? 'EN' : 'BM'}
              </button>

              {/* Streak badge */}
              <StreakBadge apiUrl={API} lang={lang} />

              {/* Notification bell */}
              <NotificationBell apiUrl={API} lang={lang} onNavigate={handleSectionChange} />

              {/* User profile pill */}
              <div className="flex items-center gap-1.5 pl-1.5">
                <div className="w-7 h-7 rounded-full bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
                  <span className="text-[11px] font-bold text-white">{(authUser?.name || studentName || '?').charAt(0).toUpperCase()}</span>
                </div>
                <span className="hidden sm:inline text-xs text-gray-600 dark:text-gray-300 font-medium max-w-[100px] truncate">{authUser?.name || studentName}</span>
                <button
                  onClick={handleLogout}
                  className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-100"
                  data-testid="logout-button"
                  title={lang === 'en' ? 'Logout' : 'Log Keluar'}
                >
                  <LogOut size={15} />
                </button>
              </div>
            </div>
          </header>

          {/* Dashboard Content */}
          <div className="dashboard-content">
            <DashboardContent
              syllabus={syllabus}
              activeSection={activeSection}
              onDeleteFile={handleDeleteFile}
              onAddNode={handleAddNode}
              onDeleteNode={handleDeleteNode}
              onRefresh={fetchSyllabus}
              apiUrl={API}
              totalOrgans={totalOrgans}
              totalFiles={totalFiles}
              totalTopics={totalTopics}
              readPoints={readPoints}
              onToggleRead={handleToggleRead}
              personalNotes={personalNotes}
              onSaveNote={handleSaveNote}
              onSectionChange={handleSectionChange}
              readProgress={readProgress}
              studentName={studentName}
              studentNiche={studentNiche}
              studentBatch={studentBatch}
              lang={lang}
              onUpdateProfile={handleUpdateProfile}
              nicheList={nicheList}
              bookmarkedIds={bookmarkedIds}
              onToggleBookmark={handleToggleBookmark}
              userRole={authUser?.role || 'student'}
              authToken={authToken}
              organAccess={organAccess}
            />
            {/* Footer */}
            <div className="py-6 pb-24 sm:pb-6 text-center">
              <p className="text-[10px] font-medium tracking-[0.18em] uppercase text-[#94A3B8]">© 2026 BSS Portal</p>
            </div>
          </div>
        </div>

        {/* Search Dialog */}
        <SearchDialog
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          syllabus={syllabus}
          onNavigate={handleSearchNavigate}
        />

        {/* Upload Dialog */}
        <UploadPdfDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          nodes={allNodes}
          apiUrl={API}
          onSuccess={handleUploadSuccess}
        />

        {/* Mobile Bottom Nav */}
        <nav className="mobile-bottom-nav" data-testid="mobile-bottom-nav">
          <button
            className={`mobile-nav-item ${activeSection === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleSectionChange('dashboard')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
            <span>Dashboard</span>
          </button>
          <button
            className={`mobile-nav-item ${organSections.some(o => o.id === activeSection) ? 'active' : ''}`}
            onClick={() => organSections.length > 0 && handleSectionChange(organSections[0].id)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
            <span>Organ</span>
          </button>
          <button
            className={`mobile-nav-item ${activeSection === 'quiz' ? 'active' : ''}`}
            onClick={() => handleSectionChange('quiz')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
            <span>Quiz</span>
          </button>
          <button
            className={`mobile-nav-item ${activeSection === 'leaderboard' ? 'active' : ''}`}
            onClick={() => handleSectionChange('leaderboard')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5C7 4 7 7 7 7"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5C17 4 17 7 17 7"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
            <span>Ranking</span>
          </button>
        </nav>

        {/* Student Name Prompt */}
        {showNamePrompt && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowNamePrompt(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-[90%] max-w-[420px] p-6" data-testid="name-prompt-dialog">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                  <User size={20} className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{lang === 'en' ? 'Student Registration' : 'Pendaftaran Pelajar'}</h3>
                  <p className="text-xs text-gray-400">{t('nameForLeaderboard', lang)}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">{lang === 'en' ? 'Full Name' : 'Nama Penuh'}</label>
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder={t('enterYourName', lang)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow duration-100"
                    autoFocus
                    data-testid="student-name-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">{lang === 'en' ? 'Business Niche' : 'Niche Bisnes'}</label>
                  <select
                    value={nicheInput}
                    onChange={(e) => setNicheInput(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow duration-100"
                    data-testid="student-niche-select"
                  >
                    <option value="">{lang === 'en' ? '-- Select your business niche --' : '-- Pilih niche bisnes anda --'}</option>
                    {nicheList.map(niche => (
                      <option key={niche} value={niche}>{niche}</option>
                    ))}
                    <option value="__custom">{lang === 'en' ? 'Others (type below)' : 'Lain-lain (taip di bawah)'}</option>
                  </select>
                </div>

                {nicheInput === '__custom' && (
                  <div>
                    <input
                      type="text"
                      placeholder={lang === 'en' ? 'Enter your business niche...' : 'Masukkan niche bisnes anda...'}
                      onChange={(e) => setNicheInput(e.target.value === '' ? '__custom' : e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow duration-100"
                      data-testid="student-niche-custom"
                    />
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Batch</label>
                  <select
                    value={batchInput}
                    onChange={(e) => setBatchInput(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow duration-100"
                    data-testid="student-batch-select"
                  >
                    <option value="">{lang === 'en' ? '-- Select your batch --' : '-- Pilih batch anda --'}</option>
                    {Array.from({ length: 21 }, (_, i) => i + 1).map(num => (
                      <option key={num} value={`BSS Batch ${num}`}>BSS Batch {num}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={async () => {
                    if (nameInput.trim()) {
                      const finalNiche = nicheInput === '__custom' ? '' : nicheInput;
                      const finalBatch = batchInput;
                      setStudentName(nameInput.trim());
                      setStudentNiche(finalNiche);
                      setStudentBatch(finalBatch);
                      saveString('bss.studentName', nameInput.trim());
                      saveString('bss.studentNiche', finalNiche);
                      saveString('bss.studentBatch', finalBatch);
                      setShowNamePrompt(false);
                      // Register on backend
                      try {
                        await axios.post(`${API}/students/register`, { name: nameInput.trim(), niche: finalNiche, batch: finalBatch });
                      } catch {}
                      toast.success(`${t('welcome', lang)}, ${nameInput.trim()}!`);
                    }
                  }}
                  disabled={!nameInput.trim()}
                  className="flex-1 py-2.5 bg-blue-500 text-white rounded-xl font-semibold text-sm hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400 transition-colors duration-150"
                  data-testid="save-name-button"
                >
                  {t('save', lang)}
                </button>
                <button
                  onClick={() => setShowNamePrompt(false)}
                  className="px-4 py-2.5 rounded-xl text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-100"
                >
                  {t('cancel', lang)}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Show name prompt on first visit */}
        {!studentName && !showNamePrompt && !loading && (
          <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 lg:bottom-6 lg:left-auto lg:right-6 lg:translate-x-0">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-3 max-w-[360px]" data-testid="name-reminder">
              <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <User size={18} className="text-blue-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{t('enterNamePrompt', lang)}</p>
                <p className="text-xs text-gray-400">{t('forLeaderboard', lang)}</p>
              </div>
              <button
                onClick={() => { setNameInput(''); setShowNamePrompt(true); }}
                className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-medium hover:bg-blue-600 flex-shrink-0 transition-colors duration-150"
              >
                {t('setName', lang)}
              </button>
            </div>
          </div>
        )}

        {/* Onboarding */}
        {showOnboarding && (
          <OnboardingModal
            lang={lang}
            organ1Unlocked={organAccess.all_unlocked || (organAccess.unlocked || []).includes(1)}
            onClose={() => { setShowOnboarding(false); saveJSON('bss.onboarded', true); try { axios.put(`${API}/profile`, { onboarded: true }); } catch {} }}
            onNavigate={(section) => {
              setShowOnboarding(false);
              saveJSON('bss.onboarded', true);
              try { axios.put(`${API}/profile`, { onboarded: true }); } catch {}
              // Navigate to Organ 1 if 'organ' is passed
              if (section === 'organ' && organSections.length > 0) {
                handleSectionChange(organSections[0].id);
              } else {
                handleSectionChange(section);
              }
            }}
          />
        )}

        <Toaster position="bottom-right" />

        {/* PWA Install Banner */}
        {showInstallBanner && (
          <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[360px] z-[100]" data-testid="pwa-install-banner">
            <div className="bg-[#0A0A0A] text-white rounded-[16px] p-4 shadow-2xl flex items-center gap-3">
              <div className="w-10 h-10 rounded-[10px] bg-white/10 flex items-center justify-center flex-shrink-0">
                <Download size={20} className="text-[#2563EB]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Install BSS Portal</p>
                <p className="text-[11px] text-white/50">Akses pantas dari home screen anda</p>
              </div>
              <button onClick={handleInstall} className="px-4 py-2 bg-[#2563EB] text-white rounded-full text-xs font-semibold hover:bg-[#1D4ED8] transition-colors flex-shrink-0" data-testid="pwa-install-btn">
                Install
              </button>
              <button onClick={dismissInstall} className="p-1 text-white/30 hover:text-white/60 transition-colors flex-shrink-0" data-testid="pwa-dismiss-btn">
                <X size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <div>
        <BrowserRouter>
          <Routes>
            <Route path="/attendance/:token" element={<AttendanceRoute />} />
            <Route path="/invite/:token" element={<SyllabusPage />} />
            <Route path="/" element={<SyllabusPage />} />
          </Routes>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

function AttendanceRoute() {
  const { token } = useParams();
  const navigate = useNavigate();
  const authToken = localStorage.getItem("bss.token") || null;
  const { AttendancePage } = require("@/components/bss/AttendancePage");
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const lang = localStorage.getItem("bss.lang") || "ms";

  const handleRequireLogin = () => {
    localStorage.setItem("bss.post_login_redirect", `/attendance/${token}`);
    navigate("/");
  };
  const handleContinue = () => {
    localStorage.removeItem("bss.post_login_redirect");
    navigate("/");
  };

  return (
    <AttendancePage
      apiUrl={API}
      authToken={authToken}
      qrToken={token}
      lang={lang}
      onRequireLogin={handleRequireLogin}
      onContinue={handleContinue}
    />
  );
}

export default App;
